<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="lg:hidden p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Ouvrir le menu"
              data-test="menu-toggle"
              @click="menuOpen = true"
            >
              <Menu class="w-5 h-5 text-gray-600" />
            </button>
            <img src="/logo.png" alt="Logo" class="h-10 w-auto" />
          </div>
          <div class="flex items-center gap-4">
            <div class="text-right">
              <p class="text-sm font-medium text-[#22282F]">{{ user?.full_name || user?.username }}</p>
              <p class="text-xs text-gray-500">{{ user?.role?.nom }}</p>
              <p class="text-xs text-gray-400">
                {{ scopeLabel }}
              </p>
            </div>
            <button
              @click="handleLogout"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Déconnexion"
            >
              <LogOut class="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <div class="flex">
      <!-- Menu latéral (grand écran) -->
      <aside
        class="hidden lg:flex flex-col w-64 shrink-0 bg-white border-r border-gray-200 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto"
        data-test="sidebar"
      >
        <NavContent :groups="groups" :path="$route.path" />
      </aside>

      <!-- Menu latéral (mobile) : tiroir -->
      <div v-if="menuOpen" class="lg:hidden fixed inset-0 z-40" data-test="drawer">
        <div class="absolute inset-0 bg-black/40" @click="menuOpen = false"></div>
        <aside class="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl flex flex-col overflow-y-auto">
          <div class="flex items-center justify-between h-16 px-4 border-b border-gray-100 shrink-0">
            <img src="/logo.png" alt="Logo" class="h-9 w-auto" />
            <button
              type="button"
              class="p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Fermer le menu"
              @click="menuOpen = false"
            >
              <X class="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <NavContent :groups="groups" :path="$route.path" />
        </aside>
      </div>

      <!-- Contenu -->
      <main class="flex-1 min-w-0">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, defineComponent, h, type PropType } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { LogOut, Menu, X, UserCircle } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'
import { buildNavGroups, isNavItemActive, type NavGroup } from '@/utils/navigation'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const user = authStore.user
const menuOpen = ref(false)

const scopeLabel = computed(() => {
  const roleCode = user?.role?.code
  if (!roleCode) return ''
  if (roleCode === 'ADMIN' && !user?.direction) return 'Administration globale'
  if ((roleCode === 'ADMIN' || roleCode === 'DIRECTEUR') && user?.direction?.nom) return user.direction.nom
  if (roleCode === 'AMBASSADEUR' && user?.ambassade?.nom) return user.ambassade.nom
  return ''
})

const groups = computed(() =>
  buildNavGroups({ roleCode: user?.role?.code, canSection: authStore.canSection })
)

// Le tiroir se referme dès qu'on change de page.
watch(() => route.path, () => { menuOpen.value = false })

const handleLogout = async () => {
  await authStore.logout()
  router.push('/')
}

// Contenu du menu, partagé par le panneau fixe et le tiroir.
const linkClass = (active: boolean) => [
  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
  active ? 'bg-[#22282F] text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-[#22282F]',
]

const NavContent = defineComponent({
  props: {
    groups: { type: Array as PropType<NavGroup[]>, required: true },
    path: { type: String, required: true },
  },
  setup(props) {
    return () =>
      h('nav', { class: 'flex flex-col flex-1 px-3 py-4', 'aria-label': 'Navigation principale' }, [
        h(
          'div',
          { class: 'flex-1 space-y-5' },
          props.groups.map((group) =>
            h('div', { key: group.id, 'data-group': group.id }, [
              h('p', { class: 'px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400' }, group.label),
              h(
                'ul',
                { class: 'space-y-0.5' },
                group.items.map((item) =>
                  h('li', { key: item.id }, [
                    h(
                      RouterLink,
                      { to: item.route, class: linkClass(isNavItemActive(props.path, item)), 'data-nav': item.id },
                      () => [h(item.icon, { class: 'w-4 h-4 shrink-0' }), h('span', item.label)]
                    ),
                  ])
                )
              ),
            ])
          )
        ),
        h('div', { class: 'pt-3 mt-4 border-t border-gray-100' }, [
          h(
            RouterLink,
            { to: '/profil', class: linkClass(props.path === '/profil'), 'data-nav': 'profil' },
            () => [h(UserCircle, { class: 'w-4 h-4 shrink-0' }), h('span', 'Mon profil')]
          ),
        ]),
      ])
  },
})
</script>

<style scoped>
</style>
